/**
 * Camps and clinics are ONE implementation, parameterized by type.
 *
 * Both are `events` rows typed 'camp' / 'clinic' (backend
 * docs/EVENT_PRIMITIVE_CONSOLIDATION.md). Until Phase B the UI was two copies
 * of the same pages — components/camps and components/clinics — that had
 * drifted: clinics had no quick-add, camps had batch registration, each had
 * fixes the other lacked. The shared implementations live in
 * components/typedEvents and are built per type from these names; the camps
 * and clinics files are the entry points routes and tests import.
 *
 * Mirrors backend src/utils/eventTypes.js typedEventNames.
 */

export type TypedEventType = "camp" | "clinic";

/**
 * The per-type names. The ones used as KEYS (noun, nouns, subtype, fk, …) are
 * typed as the union of their literal values, so `row[T.subtype]` type-checks
 * against an interface that declares both `camp_type` and `clinic_type`.
 */
export interface TypedEventNames {
  /** The stored events.event_type value. */
  type: TypedEventType;
  noun: TypedEventType; // "camp"
  nouns: `${TypedEventType}s`; // "camps"
  Noun: string; // "Camp"
  Nouns: string; // "Camps"
  NOUN: string; // "CAMP"
  NOUNS: string; // "CAMPS"
  /** The legacy subtype field the API returns: camp_type / clinic_type. */
  subtype: `${TypedEventType}_type`;
  /** The foreign key registrations carry: camp_id / clinic_id. */
  fk: `${TypedEventType}_id`;
  /** The route/body id name: campId / clinicId. */
  idParam: `${TypedEventType}Id`;
  /** Who attends, in copy: "camper" / "participant". */
  participant: string;
  participants: string;
}

// A camp has campers; a clinic has participants. The clinics copy of the
// camps UI said "camper" because it was never renamed.
const PARTICIPANT: Record<TypedEventType, string> = { camp: "camper", clinic: "participant" };

export function typedEventNames(type: TypedEventType): TypedEventNames {
  const Noun = type[0].toUpperCase() + type.slice(1);
  return Object.freeze({
    type,
    noun: type,
    nouns: `${type}s` as const,
    Noun,
    Nouns: `${Noun}s`,
    NOUN: type.toUpperCase(),
    NOUNS: `${type}s`.toUpperCase(),
    subtype: `${type}_type` as const,
    fk: `${type}_id` as const,
    idParam: `${type}Id` as const,
    participant: PARTICIPANT[type],
    participants: `${PARTICIPANT[type]}s`,
  });
}

/**
 * Props for a component that receives the event under its type's own name —
 * `camp={…}` for camps, `clinic={…}` for clinics, the contract those
 * components (and their tests) already had. `typedEventProp(T, row)` builds
 * it; `props[T.noun]` reads it.
 */
export type TypedEventProp<V> = Partial<Record<TypedEventType, V>>;

export function typedEventProp<V>(T: TypedEventNames, value: V): TypedEventProp<V> {
  return { [T.noun]: value } as TypedEventProp<V>;
}

/**
 * Pick the per-type implementation of something that still has one per type
 * (a legacy API module, a sibling entry-point component). Exhaustive by
 * construction: adding a type without supplying its entry fails to compile.
 */
export function byType<V>(type: TypedEventType, options: Record<TypedEventType, V>): V {
  return options[type];
}
